import { bulkPackPrice } from "@cafe/shared";
import { Badge, Button, Card, CardHeader, DataTable, EmptyState, Field, Modal, MoneyInput, NumberInput, PageHeader, SegmentedControl, SelectionBar, Switch, toast } from "@cafe/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpenCheck } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { dt, errorText, http, INVOICE_STATUS, money, useCan } from "../lib/api";

type Pack = { code: string; name: string; tagline: string; items: number; prices: Record<string, number>; isPublic: boolean; paid: number; pending: number; revenue: { currency: string; amount: number }[] };
type Purchase = {
  id: string; companyId: string; companyName: string | null; packCode: string; packName: string; status: "pending" | "paid"; source: string; amount: number; currency: string;
  createdAt: string; paidAt: string | null; importCount: number; invoice: { number: string; status: string } | null;
};

/** Цена и видимость набора: пусто — не продаётся в сомах, 0 — бесплатно (открывается сразу). */
function PackModal({ pack, onClose }: { pack: Pack; onClose: () => void }) {
  const qc = useQueryClient();
  const [price, setPrice] = useState<number | null>(pack.prices.KGS ?? null);
  const [isPublic, setPublic] = useState(pack.isPublic);
  const save = async () => {
    try {
      const { KGS: _, ...rest } = pack.prices;
      await http.put(`/menu-packs/${pack.code}`, { prices: price === null ? rest : { ...rest, KGS: price }, isPublic });
      toast.success("Сохранено");
      void qc.invalidateQueries({ queryKey: ["menu-packs"] });
      onClose();
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Готовое меню «${pack.name}»`}
      footer={<><Button onClick={() => void save()}>Сохранить</Button><Button variant="outline" onClick={onClose}>Отмена</Button></>}>
      <div className="flex flex-col gap-4">
        <Field label="Цена, сом" hint="Разовая покупка. 0 — бесплатно (набор открывается сразу), пусто — не продаётся. Выставленные счета не меняются">
          <MoneyInput value={price} onValueChange={setPrice} />
        </Field>
        <Switch checked={isPublic} onCheckedChange={setPublic} label="Видно клиентам" description="Скрытый набор не показывается на витрине; кто уже купил — видит его" />
      </div>
    </Modal>
  );
}

const priceText = (kgs: number | undefined) => (kgs === undefined ? "не продаётся" : kgs === 0 ? "бесплатно" : money(kgs));

/** Цена сразу у нескольких наборов: задать одну или изменить на % / сумму; с предпросмотром «было → станет». */
function BulkPriceModal({ packs, onClose, onDone }: { packs: Pack[]; onClose: () => void; onDone: () => void }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"set" | "percent" | "amount">("set");
  const [dir, setDir] = useState<"up" | "down">("up");
  const [value, setValue] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const change = value === null || (mode !== "set" && value === 0) ? null : { mode, value: mode === "set" || dir === "up" ? value : -value };
  const save = async () => {
    if (!change) return;
    setBusy(true);
    try {
      const r = await http.post<{ updated: number; skipped: number }>("/menu-packs/bulk", { codes: packs.map((p) => p.code), price: change });
      toast.success(r.skipped ? `Цена изменена у ${r.updated - r.skipped}, без цены (не продаются) — пропущено ${r.skipped}` : `Цена изменена у ${r.updated}`);
      void qc.invalidateQueries({ queryKey: ["menu-packs"] });
      onDone();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Цена: ${packs.length} набор(ов)`}
      footer={<><Button onClick={() => void save()} loading={busy} disabled={!change}>Сохранить</Button><Button variant="outline" onClick={onClose}>Отмена</Button></>}>
      <div className="flex flex-col gap-4">
        <SegmentedControl value={mode} onValueChange={(m) => { setMode(m); setValue(null); }} fullWidth aria-label="Как менять"
          options={[{ value: "set", label: "Задать цену" }, { value: "percent", label: "На процент" }, { value: "amount", label: "На сумму" }]} />
        {mode !== "set" && (
          <SegmentedControl value={dir} onValueChange={setDir} fullWidth aria-label="Направление" options={[{ value: "up", label: "Повысить" }, { value: "down", label: "Снизить" }]} />
        )}
        {mode === "percent" ? (
          <Field label="Процент" hint="Новая цена округляется до сома">
            <NumberInput value={value} onValueChange={setValue} min={1} max={dir === "up" ? 500 : 90} suffix="%" autoFocus />
          </Field>
        ) : (
          <Field label={mode === "set" ? "Цена, сом" : "Сумма, сом"} hint={mode === "set" ? "0 — бесплатно. Выставленные счета не меняются" : "Цена не станет меньше 0. Выставленные счета не меняются"}>
            <MoneyInput value={value} onValueChange={setValue} min={mode === "set" ? 0 : 1} autoFocus />
          </Field>
        )}
        <ul className="flex max-h-64 flex-col divide-y divide-border overflow-y-auto rounded-tile border border-border text-sm">
          {packs.map((p) => {
            const next = change ? bulkPackPrice(p.prices.KGS, change) : p.prices.KGS;
            return (
              <li key={p.code} className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="min-w-0 truncate">{p.name}</span>
                <span className="shrink-0 tabular-nums">
                  <span className="text-muted">{priceText(p.prices.KGS)}</span>
                  {change && <> → <span className="font-semibold">{priceText(next)}</span></>}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </Modal>
  );
}

/** «Готовые меню»: наборы по формату заведения (меню с фото, ТТК и калькуляцией), цены, продажи. Подарить набор — в карточке клиента. */
export default function MenuPacksPage() {
  const can = useCan();
  const qc = useQueryClient();
  const packs = useQuery({ queryKey: ["menu-packs"], queryFn: () => http.get<Pack[]>("/menu-packs") });
  const purchases = useQuery({ queryKey: ["menu-packs", "purchases"], queryFn: () => http.get<Purchase[]>("/menu-packs/purchases") });
  const [edit, setEdit] = useState<Pack | null>(null);
  // Чек-лист: цена и видимость сразу у нескольких наборов
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkPrice, setBulkPrice] = useState(false);
  const picked = (packs.data ?? []).filter((p) => selected.includes(p.code));
  const setPublic = async (isPublic: boolean) => {
    try {
      await http.post("/menu-packs/bulk", { codes: picked.map((p) => p.code), isPublic });
      toast.success(isPublic ? `На витрине: ${picked.length}` : `Скрыто: ${picked.length}`);
      void qc.invalidateQueries({ queryKey: ["menu-packs"] });
      setSelected([]);
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Готовые меню" description="Наборы для кофеен, шаурмичных, кафе-мороженого и других форматов: блюда с фото и описанием, продукты, полуфабрикаты и техкарты. Клиент покупает набор один раз и загружает в своё меню." />
      <Card>
        <CardHeader title="Наборы" description={can("catalog.manage") ? "Отметьте наборы, чтобы изменить цену или видимость сразу у всех; «Изменить» в строке — один набор." : undefined} />
        <DataTable rows={packs.data} loading={packs.isPending} rowKey={(p) => p.code} {...(can("catalog.manage") && { selected, onSelectedChange: setSelected })} empty={<EmptyState icon={<BookOpenCheck />} title="Наборов нет" />}
          columns={[
            { key: "name", header: "Набор", cell: (p) => <div><div className="font-medium">{p.name}</div><div className="text-sm text-muted">{p.tagline}</div></div>, mobile: "title" },
            { key: "items", header: "Блюд", align: "end", cell: (p) => p.items },
            { key: "price", header: "Цена", align: "end", cell: (p) => priceText(p.prices.KGS) },
            { key: "public", header: "Витрина", cell: (p) => <Badge tone={p.isPublic ? "success" : "neutral"}>{p.isPublic ? "Видно" : "Скрыт"}</Badge> },
            { key: "sold", header: "Куплено / ждут оплаты", align: "end", cell: (p) => `${p.paid} / ${p.pending}` },
            { key: "revenue", header: "Выручка", align: "end", cell: (p) => (p.revenue.length ? p.revenue.map((r) => money(r.amount, r.currency)).join(", ") : "—") },
          ]}
          actions={(p) => can("catalog.manage") && <Button size="sm" variant="outline" onClick={() => setEdit(p)}>Изменить</Button>} />
        <div className="px-4 sm:px-5">
          <SelectionBar count={picked.length} onClear={() => setSelected([])}>
            <Button size="sm" variant="secondary" onClick={() => setBulkPrice(true)}>Изменить цену</Button>
            <Button size="sm" variant="ghost" onClick={() => void setPublic(true)}>Показать на витрине</Button>
            <Button size="sm" variant="ghost" onClick={() => void setPublic(false)}>Скрыть</Button>
          </SelectionBar>
        </div>
      </Card>
      <Card>
        <CardHeader title="Покупки" description="Последние 300. Подарить набор клиенту — в карточке компании, раздел «Готовые меню»." />
        <DataTable rows={purchases.data} loading={purchases.isPending} rowKey={(p) => p.id} empty={<EmptyState icon={<BookOpenCheck />} title="Покупок пока нет" />}
          columns={[
            { key: "company", header: "Клиент", cell: (p) => <Link className="font-medium text-primary hover:underline" to={`/companies/${p.companyId}`}>{p.companyName ?? "—"}</Link>, mobile: "title" },
            { key: "pack", header: "Набор", cell: (p) => p.packName, mobile: "subtitle" },
            { key: "status", header: "Статус", cell: (p) => <Badge tone={p.status === "paid" ? "success" : "warning"}>{p.status === "paid" ? (p.source === "admin" && !p.amount ? "Подарен" : "Оплачен") : "Ждёт оплаты"}</Badge> },
            { key: "invoice", header: "Счёт", cell: (p) => (p.invoice ? <span>{p.invoice.number} <span className="text-muted">({INVOICE_STATUS[p.invoice.status]?.label ?? p.invoice.status})</span></span> : "—") },
            { key: "amount", header: "Сумма", align: "end", cell: (p) => money(p.amount, p.currency) },
            { key: "date", header: "Дата", cell: (p) => dt(p.paidAt ?? p.createdAt) },
            { key: "imports", header: "Загрузок", align: "end", cell: (p) => p.importCount },
          ]} />
      </Card>
      {edit && <PackModal pack={edit} onClose={() => setEdit(null)} />}
      {bulkPrice && <BulkPriceModal packs={picked} onClose={() => setBulkPrice(false)} onDone={() => { setBulkPrice(false); setSelected([]); }} />}
    </div>
  );
}
